import {useCallback, useEffect, useRef, useState} from 'react';

import {useQuery} from '@apollo/react-hooks';
import {NetworkStatus} from 'apollo-client';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetRelatedAccounts as GetRelatedAccountsQuery,
    GetRelatedAccounts_contact_accounts as Accounts,
    GetRelatedAccountsVariables,
    PeepRelatedPeepSortType,
} from 'nutshell-graphql-types';

import GET_RELATED_ACCOUNTS from '../queries/get-related-accounts.graphql';

const INITIAL_VISIBLE_ACCOUNTS = 5;
const EXPANDED_VISIBLE_ACCOUNTS = 1000;

export function useGetRelatedAccounts(
    contactId: string,
    formerOnly: boolean,
    skip: boolean = false,
    search?: string | null,
    sortBy?: PeepRelatedPeepSortType | null
): {
    accounts: Accounts | null;
    totalCount: number;
    isLoading: boolean;
    isLoadingMore: boolean;
    isRefreshing: boolean;
    isRefetching: boolean;
    isExpanded: boolean;
    hasNextPage: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchAllAccounts: () => void;
} {
    const [requestedFirst, setRequestedFirst] = useState<number>(INITIAL_VISIBLE_ACCOUNTS);

    const prevSearchRef = useRef<string | null | undefined>(search);
    const prevSortByRef = useRef<PeepRelatedPeepSortType | null | undefined>(sortBy);
    const fetchAllRef = useRef<boolean>(false);

    const hasSearchChanged = prevSearchRef.current !== search;
    const hasSortChanged = prevSortByRef.current !== sortBy;

    if (hasSearchChanged || hasSortChanged) {
        fetchAllRef.current = false;
    }

    // Reset to collapsed state when search changes
    if (hasSearchChanged) {
        prevSearchRef.current = search;
        if (requestedFirst !== INITIAL_VISIBLE_ACCOUNTS) {
            setRequestedFirst(INITIAL_VISIBLE_ACCOUNTS);
        }
    }

    if (hasSortChanged) {
        prevSortByRef.current = sortBy;
    }

    const {data, loading, error, refetch, networkStatus} = useQuery<
        GetRelatedAccountsQuery,
        GetRelatedAccountsVariables
    >(GET_RELATED_ACCOUNTS, {
        variables: {
            id: contactId,
            formerOnly,
            first: requestedFirst,
            search,
            sortBy,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip,
    });

    // Reset fetchAllRef when loading is complete
    const prevLoadingRef = useRef(loading);
    useEffect(() => {
        if (prevLoadingRef.current && !loading) {
            fetchAllRef.current = false;
        }
        prevLoadingRef.current = loading;
    }, [loading]);

    const accounts = data?.contact?.accounts ?? null;
    const totalCount = accounts?.totalCount ?? 0;
    const currentCount = accounts?.edges.length ?? 0;
    const hasNextPage = accounts?.pageInfo?.hasNextPage ?? false;
    const isExpanded = currentCount > INITIAL_VISIBLE_ACCOUNTS;

    const fetchAllAccounts = useCallback(() => {
        if (totalCount > INITIAL_VISIBLE_ACCOUNTS) {
            fetchAllRef.current = true;
            setRequestedFirst(EXPANDED_VISIBLE_ACCOUNTS);
        }
    }, [totalCount]);

    // When expanded, use the full expanded page size so that newly added items are included.
    // When not expanded, use the initial page size.
    const refetchWithCurrentCount = useCallback(() => {
        const first = isExpanded ? requestedFirst : INITIAL_VISIBLE_ACCOUNTS;
        return refetch({id: contactId, formerOnly, first, search, sortBy});
    }, [contactId, formerOnly, isExpanded, requestedFirst, search, sortBy, refetch]);

    return {
        accounts,
        totalCount,
        isLoading: loading && !accounts,
        isLoadingMore: loading && fetchAllRef.current,
        isRefreshing: loading && Boolean(accounts) && !fetchAllRef.current,
        isRefetching: networkStatus === NetworkStatus.refetch,
        isExpanded,
        hasNextPage,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch: refetchWithCurrentCount,
        fetchAllAccounts,
    };
}
